import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, EDIT } from "const"
import { CcApprovalLevel } from "server/models"
import { StateType, deepCopy, hasAccess, hasProjectAccess } from "client/utils"
import {
    PRM_ACCOUNT_SETTINGS,
    PRM_PROJECT_SETTINGS,
    StoreType,
    CcApprovalLevelList,

    LoadCcApprovalLevels,
    DeleteCcApprovalLevel,
    DragnDropCcApprovalLevel,

    setCcApprovalLevelList,
    setCcModuleApprovalLevelList,
    addModal,
} from "client/store"
import {
    Container,
    Loader,
    Button,
    Table,
    DeleteRow,
    DragnDrop,
} from "client/components"

import Columns from "./columns"
import Edit from "../edit"

interface ApprovalLevelListProps {
    module: number
}

const List = ({
    module,
}: ApprovalLevelListProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const levelList = useSelector((state: StoreType) => state.ccApprovalLevel.levelList)
    const moduleLevelList = useSelector((state: StoreType) => state.ccApprovalLevel.moduleLevelList)
    const reloadList = useSelector((state: StoreType) => state.ccApprovalLevel.reloadList)

    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)
    const projectSettings = useSelector((state: StoreType) => state.project.projectSettings)
    const defaultAssigned = project.id
        ? projectSettings.cc.defaultApprovalLevelsAssigned
        : accountSettings.project.defaultGroupsAssigned

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    const LoadData = () => {
        setLoadState(PENDING)
        LoadCcApprovalLevels(userData.accountId, project.id, module)
        .then((res) => {
            dispatch(setCcApprovalLevelList(res.levelList))
            dispatch(setCcModuleApprovalLevelList({ [module]: res.levelIds }))
            setLoadState(IDLE)
        })
    }

    useEffect(() => {
        if (!reloadList) return
        LoadData()
    }, [reloadList, project, defaultAssigned])

    const onDelete: DeleteRow = (level: CcApprovalLevel, callback) => {
        DeleteCcApprovalLevel(level.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const levelListCopy: CcApprovalLevelList = levelList ? deepCopy(levelList) : {}
                delete levelListCopy[level.id]
                dispatch(setCcApprovalLevelList(levelListCopy))
                dispatch(setCcModuleApprovalLevelList({
                    [module]: moduleLevelList[module].filter((id) => id !== level.id),
                }))
            })
        })
    }

    const onDragnDrop: DragnDrop = (dataIds, callback) => {
        dispatch(setCcModuleApprovalLevelList({ [module]: dataIds }))
        setLoadState(PENDING)
        DragnDropCcApprovalLevel(dataIds, levelList).then((success) => {
            LoadData()
            callback(success)
        })
    }

    const editAccess = !project.id
        ? hasAccess(PRM_ACCOUNT_SETTINGS, EDIT)
        : hasProjectAccess(PRM_PROJECT_SETTINGS, EDIT)

    const columns = Columns(editAccess, module)
    return (
        <Container>
            {
                loadState === PENDING
                && <Loader />
            }
            <Table
                columns={columns}
                dataList={levelList}
                dataIds={moduleLevelList[module]}
                rowSize="sm"
                showPagination={false}
                onDelete={onDelete}
                onDragnDrop={onDragnDrop}
                bottomContent={editAccess && (
                    <Button
                        variant="success"
                        onClick={() => dispatch(addModal({
                            key: "addApprovalLevel",
                            content: <Edit levelId={0} module={module} />,
                        }))}
                    >
                        {lang.add}
                    </Button>
                )}
            />
        </Container>
    )
}
export default List
